import { api } from '@/integrations/clients/carAuction/carAuctionClient';
import {
  ListingOverview,
  ListingLeadbox,
  GetAllListingsResponse,
  GetListingDetailsResponse,
  ListingQueryParams,
  CreateListingRequest,
  UpdateListingRequest,
} from '@/types/listing';
import { PaymentWithDetails } from '@/types/payment';
import { Transport } from '@/types/transport';
import { UserDetails } from '@/types/userDetails';
import { TestService } from '@/services/test/testService';
import { UserService } from '@/services/user/userService';
import { PaymentService } from '@/services/payment/paymentService';
import { TransportService } from '@/services/transport/transportService';

// Toggle to use mocked listing flows (no backend required)
const USE_MOCK_LISTING = false;

export class ListingManagementService {
  // Get all listings with optional filters (same as listingService for admin overview)
  static async getAllListings(params: ListingQueryParams = {}): Promise<GetAllListingsResponse> {
    // This method is identical to the one in listingService
    // It's duplicated here to maintain separation of concerns
    if (USE_MOCK_LISTING) {
      await new Promise((r) => setTimeout(r, 100));
      
      // Import the mock data from listingService to avoid duplication
      const { ListingService } = await import('./listingService');
      return await ListingService.getAllListings(params);
    }

    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.status) queryParams.append('status', Array.isArray(params.status) ? params.status.join(',') : params.status);
    if (params.listingType) queryParams.append('listingType', params.listingType);
    if (params.seller) queryParams.append('seller', params.seller);
    if (params.winner) queryParams.append('winner', params.winner);
    if (params.campaignId) queryParams.append('campaignId', params.campaignId);
    if (params.regNumber) queryParams.append('regNumber', params.regNumber);
    if (params.vin) queryParams.append('vin', params.vin);
    if (params.sort) queryParams.append('sort', params.sort);

    // Handle search - send as filters.search
    if (params.search) {
      queryParams.append('filters[search]', params.search);
    }

    const response = await api.get<{
      success: boolean;
      data: ListingLeadbox[];
      count: number;
      pagination?: {
        page?: number;
        limit?: number;
        total?: number;
        totalPages?: number;
        next?: { page: number; limit: number };
        prev?: { page: number; limit: number };
      };
    }>(`/admin/listings?${queryParams.toString()}`);
    
    // count = total matching rows (filters applied); data = current page only
    const listings: ListingOverview[] = (response.data || []).map((listing) => ({
      ...listing,
      isFavorite: false,
    }));
    const limit = params.limit || response.pagination?.limit || 25;
    const page = params.page || response.pagination?.page || 1;
    const totalCount = response.pagination?.total ?? response.count ?? 0;
    
    return {
      listings,
      data: listings,
      totalCount,
      totalPages:
        response.pagination?.totalPages ||
        Math.max(1, Math.ceil(totalCount / limit)),
      page,
      limit,
    };
  }

  // Get listing with detailed information (for admin) - returns ListingLeadbox format
  static async getListingDetails(listingId: string): Promise<GetListingDetailsResponse> {
    if (USE_MOCK_LISTING) {
      await new Promise((r) => setTimeout(r, 100));

      // Get the base listing with vehicle test from listingService
      const { ListingService } = await import('./listingService');
      const listingResponse = await ListingService.getListing(listingId); // No userId needed for admin
      const listingWithVehicleTest = listingResponse.listing;
      
      // Get the original listing data to access thumbnail
      const allListingsResponse = await ListingService.getAllListings({ limit: 100 });
      const originalListing = allListingsResponse.listings.find(listing => listing.id === listingId);
      
      // Fetch payment and transport data from services
      const payments = await PaymentService.getPaymentsByAuctionWithDetails(listingId);
      const transports = await TransportService.getTransportsByAuction(listingId);
      
      // Use fetched data or fallback to empty arrays if none exists
      const paymentData: PaymentWithDetails[] = payments.length > 0 ? payments : [];
      const transportData: Transport[] = transports.length > 0 ? transports : [];

      // Fetch seller and winner data from UserManagementService
      const { UserManagementService } = await import('../user/userManagementService');
      const seller = await UserManagementService.getUserById(listingWithVehicleTest.sellerId);
      const winner = listingWithVehicleTest.winnerId 
        ? await UserManagementService.getUserById(listingWithVehicleTest.winnerId)
        : undefined;

      // Use UserDetails directly (no conversion needed)
      const sellerData: UserDetails = seller;
      const winnerData: UserDetails | undefined = winner;

      // Create ListingWithDetails
      const listingWithDetails: ListingLeadbox = {
        ...listingWithVehicleTest,
        winnerId: listingWithVehicleTest.winnerId,
        payment: paymentData,
        transport: transportData,
        seller: sellerData,
        winner: winnerData,
        thumbnail: listingWithVehicleTest.vehicleTest.images.exterior?.find(img => img.position === 'thumbnail')?.url || originalListing?.thumbnail || '',
        vehicleTestId: listingWithVehicleTest.vehicleTest.id,
      };

      return { listing: listingWithDetails };
    }

    const response = await api.get<GetListingDetailsResponse>(`/admin/listings/${listingId}/details`);
    return response;
  }

  // Create a new listing
  static async createListing(listingData: CreateListingRequest): Promise<void> {
    // if (USE_MOCK_LISTING) {
    //   await new Promise((r) => setTimeout(r, 800));
    //   return { id: 'new-listing-id' };
    // }

    await api.post('/admin/listings', listingData);
  }

  // Update an existing listing
  static async updateListing(updateData: UpdateListingRequest): Promise<{ message: string }> {
    if (USE_MOCK_LISTING) {
      await new Promise((r) => setTimeout(r, 600));
      return { message: 'Listing updated successfully' };
    }

    const { id, ...data } = updateData;
    const response = await api.patch<{ message: string }>(`/admin/listings/${id}`, data);
    return response;
  }

  /** Save manual price intervals and flag listing to skip AutoUncle. */
  static async saveListingRatingIntervals(
    listingId: string,
    intervals: Array<{ label: string; upperBound: number; lowerBound: number }>,
  ): Promise<void> {
    await api.post(`/admin/listings/${listingId}/rating-intervals`, { intervals });
  }

  // Delete a listing
  static async deleteListing(listingId: string): Promise<{ message: string }> {
    if (USE_MOCK_LISTING) {
      await new Promise((r) => setTimeout(r, 500));
      return { message: 'Listing deleted successfully' };
    }

    const response = await api.delete<{ message: string }>(`/admin/listings/${listingId}`);
    return response;
  }

  // Get cancel preview for listing
  static async getCancelPreview(listingId: string): Promise<{
    bidCount: number;
    hasActiveBids: boolean;
    listingTitle: string;
    listingType: string;
    listingStatus: string;
  }> {
    if (USE_MOCK_LISTING) {
      await new Promise((r) => setTimeout(r, 500));
      return {
        bidCount: 0,
        hasActiveBids: false,
        listingTitle: "Mock Listing",
        listingType: "auction",
        listingStatus: "active",
      };
    }

    const response = await api.get<{
      success: boolean;
      bidCount: number;
      hasActiveBids: boolean;
      listingTitle: string;
      listingType: string;
      listingStatus: string;
    }>(`/admin/listings/${listingId}/cancel-preview`);
    return response;
  }

  // Cancel listing
  static async cancelListing(listingId: string): Promise<{
    bidCount: number;
  }> {
    if (USE_MOCK_LISTING) {
      await new Promise((r) => setTimeout(r, 500));
      return { bidCount: 0 };
    }

    const response = await api.post<{
      success: boolean;
      data: any;
      bidCount: number;
    }>(`/admin/listings/${listingId}/cancel`);
    return { bidCount: response.bidCount || 0 };
  }

  // Activate reserved listing
  static async activateReservedListing(listingId: string): Promise<void> {
    if (USE_MOCK_LISTING) {
      await new Promise((r) => setTimeout(r, 500));
      return;
    }

    await api.post(`/admin/listings/${listingId}/activate`);
  }

  // Complete reserved listing
  static async completeReservedListing(listingId: string): Promise<void> {
    if (USE_MOCK_LISTING) {
      await new Promise((r) => setTimeout(r, 500));
      return;
    }

    await api.post(`/admin/listings/${listingId}/complete`);
  }

  // Confirm buyer delivery booking
  static async confirmBuyerDelivery(listingId: string): Promise<void> {
    if (USE_MOCK_LISTING) {
      await new Promise((r) => setTimeout(r, 500));
      return;
    }

    await api.post(`/admin/listings/${listingId}/buyer-delivery/confirm`);
  }

  /** Queue payment reminder email to buyer (admin only) */
  static async sendBuyerPaymentReminder(listingId: string): Promise<void> {
    if (USE_MOCK_LISTING) {
      await new Promise((r) => setTimeout(r, 300));
      return;
    }
    await api.post(`/admin/listings/${listingId}/buyer-payment-reminder`);
  }
}
